import { memo, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AdSlot } from '@/components/AdSlot';
import { formatDrinkDisplayName } from '@/lib/displayName';
import { formatMetric, mlToFlOz } from '@/lib/metrics';
import { formatPackagePrice } from '@/lib/packageLabel';
import type { CatalogRow } from '@/lib/catalog';
import { useCatalog } from '@/hooks/CatalogContext';
import { SortableMetricHeader } from '@/components/RatioHeader';
import type { VolumeUnit } from '@/types/data';

function alcoholUnitLabel(unit: VolumeUnit): string {
  return unit === 'fl_oz' ? 'fl oz' : 'mL';
}

const AD_EVERY = 25;
const COL_COUNT = 8;
const DRINK_ROW_ESTIMATE = 88;
const AD_ROW_ESTIMATE = 120;

type FlatItem =
  | { kind: 'drink'; key: string; row: CatalogRow }
  | { kind: 'ad'; key: string };

function buildFlatItems(rows: CatalogRow[]): FlatItem[] {
  const items: FlatItem[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    items.push({ kind: 'drink', key: row.product.id, row });
    if ((i + 1) % AD_EVERY === 0 && i + 1 < rows.length) {
      items.push({ kind: 'ad', key: `ad-after-${i}` });
    }
  }
  return items;
}

function CatalogTableRow({
  row,
  unit,
}: {
  row: CatalogRow;
  unit: VolumeUnit;
}) {
  const cal =
    unit === 'fl_oz'
      ? row.serving.alcoholPer100CaloriesFlOz
      : row.alcoholPer100Calories;
  const dollar =
    unit === 'fl_oz'
      ? row.package.alcoholPerDollarFlOz
      : row.alcoholPerDollar;
  const serving =
    unit === 'fl_oz' && row.nutrition
      ? `${formatMetric(mlToFlOz(row.nutrition.servingMl), 2)} fl oz`
      : row.nutrition
        ? `${formatMetric(row.nutrition.servingMl, 0)} mL`
        : '—';

  const isRecipe =
    row.product.category === 'mixed-drinks' ||
    row.product.brand === 'Recipe';
  const displayName = formatDrinkDisplayName(row.product.name);
  const brand = formatDrinkDisplayName(row.product.brand?.trim() ?? '');
  const showBrand =
    Boolean(brand) &&
    !isRecipe &&
    brand.toLowerCase() !== displayName.trim().toLowerCase();

  return (
    <>
      <td>
        <div className="drink-name-cell">
          <strong>
            <Link to={`/drinks/${row.product.slug}/`} title="See details">
              {displayName}
            </Link>
          </strong>
          {showBrand && (
            <div className="muted" style={{ fontSize: '0.8rem' }}>
              {brand}
            </div>
          )}
          <Link
            className="drink-compare-link"
            to={`/compare/?c=${row.product.id}`}
            title="Compare this drink"
          >
            Compare
          </Link>
        </div>
      </td>
      <td>
        {row.product.category}
        <div className="muted" style={{ fontSize: '0.8rem' }}>
          {row.product.subtype}
        </div>
      </td>
      <td>{serving}</td>
      <td>
        {row.product.abvPercent != null ? `${row.product.abvPercent}%` : '—'}
      </td>
      <td>
        {row.nutrition?.calories ?? '—'}
        {row.nutrition && (
          <div>
            <span
              className={`badge badge--${row.nutrition.status === 'verified' ? 'verified' : 'estimated'}`}
            >
              {row.nutrition.status}
            </span>
          </div>
        )}
      </td>
      <td className="metric">
        {formatMetric(cal)}
        {cal == null && row.missingReason && (
          <span className="metric-unit">{row.missingReason}</span>
        )}
      </td>
      <td className="metric">
        {formatMetric(dollar)}
        {dollar == null && (
          <span className="metric-unit">
            {row.price.packagePriceUsd == null ? 'Price unavailable' : ''}
          </span>
        )}
      </td>
      <td>
        <div className="price-cell">
          <span
            className={`badge ${row.price.isUserOverride ? 'badge--user' : ''}`}
          >
            {row.price.label}
          </span>
          <div className="price-cell__pkg">
            {formatPackagePrice(
              row.price.packagePriceUsd,
              row.price.containerCount,
              row.price.containerMl,
              { category: row.product.category, volumeUnit: unit },
            )}
          </div>
        </div>
      </td>
    </>
  );
}

const MemoRow = memo(CatalogTableRow);

export function CatalogTable({ rows }: { rows: CatalogRow[] }) {
  const { prefs } = useCatalog();
  const unit = prefs.volumeUnit;
  const parentRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => buildFlatItems(rows), [rows]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      items[index]?.kind === 'ad' ? AD_ROW_ESTIMATE : DRINK_ROW_ESTIMATE,
    overscan: 12,
  });

  useEffect(() => {
    parentRef.current?.scrollTo({ top: 0 });
  }, [rows]);

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom =
    virtualizer.getTotalSize() -
    (virtualRows[virtualRows.length - 1]?.end ?? 0);

  return (
    <div className="table-wrap table-wrap--virtual" ref={parentRef}>
      <table className="catalog-table">
        <thead>
          <tr>
            <th>Drink</th>
            <th>Type</th>
            <th>Serving</th>
            <th>ABV</th>
            <th>Calories</th>
            <SortableMetricHeader
              sortKey="alcoholPer100Calories"
              label={`Alcohol (${alcoholUnitLabel(unit)}) / 100 cal`}
              title={`Pure alcohol (${alcoholUnitLabel(unit)}) for the same 100 calories. Higher means fewer calories for equivalent alcohol.`}
            />
            <SortableMetricHeader
              sortKey="alcoholPerDollar"
              label={`Alcohol (${alcoholUnitLabel(unit)}) / $`}
              title={`Pure alcohol (${alcoholUnitLabel(unit)}) per dollar of the selected package. Higher is better value.`}
            />
            <th>Price basis</th>
          </tr>
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr aria-hidden="true" className="catalog-virtual-spacer">
              <td
                colSpan={COL_COUNT}
                style={{ height: paddingTop, padding: 0, border: 0 }}
              />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const item = items[virtualRow.index]!;
            if (item.kind === 'ad') {
              return (
                <tr
                  key={item.key}
                  className="catalog-ad-row"
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                >
                  <td colSpan={COL_COUNT}>
                    <AdSlot slot="inFeed" label="Advertisement" />
                  </td>
                </tr>
              );
            }
            return (
              <tr
                key={item.key}
                id={`drink-${item.row.product.id}`}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
              >
                <MemoRow row={item.row} unit={unit} />
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true" className="catalog-virtual-spacer">
              <td
                colSpan={COL_COUNT}
                style={{ height: paddingBottom, padding: 0, border: 0 }}
              />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
